<script setup lang="ts">
    import { ref, computed, onMounted, watch, shallowReactive } from 'vue'
    import { useRoute } from 'vue-router'
    import CorporateNavigation from "@common/Components/CorporateNavigation.vue"
    import ProjectPlanTemplateEditor from '@Quest/ProjectPlanTemplates/Components/ProjectPlanTemplateEditor.vue'

    import { ProjectPlanTemplateData, ProjectPlanTemplateEditorViewModel } from '@Quest/ProjectPlanTemplates/Components/ProjectPlanTemplateEditor'
    import type { ProjectPlanTemplateReadModel, ProjectPlanTemplatesPageReadModel } from '@Quest/ProjectPlanTemplates/readmodels'
    import { useProjectPlanTemplatesPageQueryV1 } from '@Quest/ProjectPlanTemplates/queries-vue-composables'
    import { useCreateProjectPlanTemplateCommandV1, useUpdateProjectPlanTemplateCommandV1, useDeleteProjectPlanTemplateCommandV1 } from '@Quest/ProjectPlanTemplates/commands-vue-composables'

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params.strategyId ?? ''))

    const projectPlanTemplateEditor = shallowReactive(new ProjectPlanTemplateEditorViewModel())

    const {
        runProjectPlanTemplatesPageQueryV1,
        modelProjectPlanTemplatesPageQueryV1: rm
    } = useProjectPlanTemplatesPageQueryV1(workspaceId, strategyId)

    const {
        invokeCreateProjectPlanTemplateCommandV1,
        succeedCreateProjectPlanTemplateCommandV1
    } = useCreateProjectPlanTemplateCommandV1(workspaceId, strategyId)

    const {
        invokeUpdateProjectPlanTemplateCommandV1,
        succeedUpdateProjectPlanTemplateCommandV1
    } = useUpdateProjectPlanTemplateCommandV1(workspaceId, strategyId)

    const {
        invokeDeleteProjectPlanTemplateCommandV1,
        succeedDeleteProjectPlanTemplateCommandV1
    } = useDeleteProjectPlanTemplateCommandV1(workspaceId, strategyId)

    watch(
        [
            succeedCreateProjectPlanTemplateCommandV1,
            succeedUpdateProjectPlanTemplateCommandV1,
            succeedDeleteProjectPlanTemplateCommandV1
        ],
        ([create, update, del]) =>
        {
            if (create || update || del)
            {
                runProjectPlanTemplatesPageQueryV1()
                projectPlanTemplateEditor.close()
            }
        }
    )

    function showProjectPlanTemplateCreator()
    {
        projectPlanTemplateEditor.showCreator()
    }

    function showProjectPlanTemplateEditor(t: ProjectPlanTemplateReadModel)
    {
        projectPlanTemplateEditor.showEditor(t)
    }

    function handleCreateProjectPlanTemplateCommand(p: ProjectPlanTemplateData)
    {
        invokeCreateProjectPlanTemplateCommandV1({
            Id: p.Id,
            name: p.Name,
            description: p.Description,
            durationInMonths: p.DurationInMonths,
            granularity: p.Granularity,
            denomination: p.Denomination
        })
    }

    function handleUpdateProjectPlanTemplateCommand(p: ProjectPlanTemplateData)
    {
        invokeUpdateProjectPlanTemplateCommandV1({
            Id: p.Id,
            name: p.Name,
            description: p.Description,
            durationInMonths: p.DurationInMonths,
            granularity: p.Granularity,
            denomination: p.Denomination
        })
    }

    function handleDeleteProjectPlanTemplateCommand(t: ProjectPlanTemplateReadModel)
    {
        if (confirm("Are you sure?"))
        {
            invokeDeleteProjectPlanTemplateCommandV1({ Id: t.Id })
        }
    }

    onMounted(() =>
    {
        document.title = "Enterprise business case templates"
        runProjectPlanTemplatesPageQueryV1()
    })
</script>

<template>
    <div>

        <ProjectPlanTemplateEditor :viewModel="projectPlanTemplateEditor" 
                                   @create-template="handleCreateProjectPlanTemplateCommand" 
                                   @update-template="handleUpdateProjectPlanTemplateCommand"/>

        <CorporateNavigation ActivePage="strategy" />

        <div v-if="rm" class="container" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Business case templates</h1>
                </div>
                <div class="col-lg-6 text-right align-self-end">
                    <button type="button" class="btn btn-outline-success" style="padding: 0.7em 2em;" @click="showProjectPlanTemplateCreator">Create a template</button>
                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0 60px;" />

            <div class="mt-3">

                <table class="table table-bordered table-hover">
                    <thead>
                        <tr>
                            <th class="align-top">Template</th>
                            <th class="align-top text-center">Granularity</th>
                            <th class="align-top text-center">Denomination</th>
                            <th class="align-top text-center">Project execution<br/> <small class="text-muted">in months</small></th>
                            <th style="width: 120px;"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="t in rm.Templates" :key="t.Id">
                            <td>
                                {{t.Name}}<br />
                                {{t.Description}}
                            </td>
                            <td class="text-center">{{t.Granularity}}</td>
                            <td class="text-center">{{t.Denomination}}</td>
                            <td class="text-center">{{t.DurationInMonths}}</td>
                            <td>
                                <div class="dropdown">
                                    <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                        Actions
                                    </button>
                                    <div class="dropdown-menu dropdown-menu-right">
                                        <a class="dropdown-item" @click="showProjectPlanTemplateEditor(t)">Edit</a>
                                        <a class="dropdown-item" @click="handleDeleteProjectPlanTemplateCommand(t)">Delete</a>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>